import { ref, get } from "firebase/database";
import { database } from "../../firebaseConfig";
import { authenticatedFetch, CLOUD_FUNCTION_URLS } from "../../utils/cloudFunctions";
import { chargeCredits } from "../../utils/creditCharge";
import { costOf } from "../../config/creditPlans";
import { parseAddress, getPreviewText } from "../InboxView/utils/emailParsing";

const THREAD_CONTEXT_CHARS = 3000;

/**
 * Drafts the cover text for a forwarded conversation from one of the user's
 * AI templates. Only returns text — the conversation itself is still embedded
 * by the regular forward send, so nothing about delivery changes.
 * Throws with a user-facing message on credit or generation failure.
 */
export async function draftForwardIntro({ userId, templateId, email, forwardTo, forwardToName, account, userSettings = {} }) {
  const snap = await get(ref(database, `World/${userId}/settings/customTopics/${templateId}`));
  const template = snap.exists() ? snap.val() : null;
  if (!template) throw new Error("This template no longer exists.");

  try {
    await chargeCredits({
      action: "inbox_agent_reply",
      amount: costOf("inboxAgentReply"),
      worldId: userId,
      metadata: { emailId: email.id, path: "task_forward_template", templateId },
    });
  } catch (err) {
    throw new Error(err.code === "insufficient_credits"
      ? `Not enough credits for the AI text (${costOf("inboxAgentReply")} required).`
      : "Could not book the credits for the AI text — please try again.");
  }

  const sender = parseAddress(email.from);
  const threadText = (getPreviewText(email) || email.bodyText || "").slice(0, THREAD_CONTEXT_CHARS);
  const notes = template.topicSpecificNotes || template.topicSpecificNote || "";
  const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.worldEmailCreator(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      userId,
      businessName: sender.name || sender.email || "",
      senderName: account?.sender_name || userSettings.user_name || "BonusClub",
      senderEmail: account?.email || "",
      recipientName: forwardToName || "",
      topic: template.topic || "Forward",
      context: [
        `You are writing the short cover text for FORWARDING an existing email conversation to ${forwardToName || forwardTo}, a new person at the same company.`,
        "The conversation is attached below your text automatically — do not repeat or quote it, only introduce it and follow the template's goal.",
        notes ? `Template instructions:\n${notes}` : "",
        `Conversation subject: ${email.subject || ""}\nFrom: ${email.from || ""}\n\nLatest message:\n${threadText}`,
      ].filter(Boolean).join("\n\n"),
      language: userSettings.language || "de",
    }),
  });
  if (!response.ok) throw new Error("The AI text could not be generated.");
  const data = await response.json();
  const text = data.emailContent || data.content || "";
  if (!text.trim()) throw new Error("The AI text came back empty.");
  return text;
}
